import { useEffect, useRef, useState } from 'react'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { BuscadorCliente, type ClienteElegido } from '@/modules/comercial/BuscadorCliente'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Modal } from '@/shared/ui/Modal/Modal'

import { Cotizador } from './Cotizador'

type PropsCotizadorLibre = {
  abierto: boolean
  onCerrar: () => void
}

/**
 * Cotizar sin orden de servicio: venta de materiales.
 *
 * Primero se escoge el cliente —no hay orden de donde deducirlo— y después se
 * arma la cotización con el mismo cotizador de siempre. No lleva mano de obra
 * obligatoria: no es un trabajo, es una venta.
 */
export function CotizadorLibre({ abierto, onCerrar }: PropsCotizadorLibre) {
  const [cliente, setCliente] = useState<ClienteElegido | null>(null)
  const [cotizacionId, setCotizacionId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [abriendo, setAbriendo] = useState(false)

  // Al cerrar se olvida todo: reabrir con el cliente de la vez pasada es la
  // forma más fácil de cotizarle al que no era.
  const estabaAbierto = useRef(abierto)
  useEffect(() => {
    if (estabaAbierto.current && !abierto) {
      setCliente(null)
      setCotizacionId(null)
      setError(null)
    }
    estabaAbierto.current = abierto
  }, [abierto])

  async function empezar() {
    if (!cliente) return

    setError(null)
    setAbriendo(true)

    try {
      const { data, error: fallo } = await supabase.rpc('fn_abrir_cotizacion_libre', {
        p_id_tercero: cliente.id_tercero,
        p_nota: null,
      })

      if (fallo) throw new Error(mensajeDeError(fallo))

      setCotizacionId(data as string)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No fue posible abrir la cotización.')
    } finally {
      setAbriendo(false)
    }
  }

  // Con el cliente escogido, se arma con el cotizador de siempre.
  if (cotizacionId && cliente) {
    return (
      <Cotizador
        abierto={abierto}
        ordenServicioId=""
        ordenNumero="Venta de materiales"
        clienteNombre={cliente.nombre}
        onCerrar={onCerrar}
        libre={{ cotizacionId }}
      />
    )
  }

  return (
    <Modal
      abierto={abierto}
      titulo="Nueva cotización"
      subtitulo="Venta de materiales, sin orden de servicio."
      onCerrar={onCerrar}
      pie={
        <>
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton
            variante="principal"
            onClick={() => void empezar()}
            trabajando={abriendo}
            disabled={!cliente}
          >
            Continuar
          </Boton>
        </>
      }
    >
      {error && (
        <div style={{ marginBottom: 'var(--e-4)' }}>
          <Aviso tipo="error">{error}</Aviso>
        </div>
      )}

      <BuscadorCliente
        elegido={cliente}
        onElegir={setCliente}
        obligatorio
        ayuda="Escriba al menos dos letras. Si el cliente no existe, se crea aquí mismo en World Office."
      />

      <div style={{ marginTop: 'var(--e-5)' }}>
        <Aviso tipo="info">
          Una cotización de materiales no lleva mano de obra obligatoria: no es un trabajo. Las de
          un trabajo se arman desde su orden de servicio.
        </Aviso>
      </div>
    </Modal>
  )
}
